import { useState } from "react";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Apple,
  FileSpreadsheet,
  FlaskConical,
  Plus,
  Ruler,
  ShieldAlert,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import { TypeList, MeasurementTypeList } from "./type-list";
import { AddMeasurementDialog } from "./add-measurement-dialog";
import { FormatSection } from "./format-section";
import { EquipmentSection } from "./equipment-section";
import { SensoryConfigSection } from "./sensory-config-section";
import { BrewSensoryConfigSection } from "@/features/brew-sensory/brew-sensory-config-section";

export const CONFIG_TABS = ["formats", "nutrients", "measurements", "allergens", "equipment", "sensory", "brew-sensory"] as const;
export type ConfigTab = (typeof CONFIG_TABS)[number];

type NutrientType = RouterOutputs["food"]["nutrientTypes"]["list"][number];
type MeasurementType = RouterOutputs["food"]["measurementTypes"]["list"][number];
type Allergen = RouterOutputs["food"]["allergens"]["list"][number];

export function ConfigurationPage({
  tab,
  onTabChange,
}: {
  tab: ConfigTab;
  onTabChange: (value: string) => void;
}) {
  const utils = trpc.useUtils();
  const [addMeasurementOpen, setAddMeasurementOpen] = useState(false);

  const { data: nutrientTypes } = trpc.food.nutrientTypes.list.useQuery();
  const { data: measurementTypes } = trpc.food.measurementTypes.list.useQuery();
  const { data: allergens } = trpc.food.allergens.list.useQuery();

  const createNutrient = trpc.food.nutrientTypes.create.useMutation({
    onSuccess: () => utils.food.nutrientTypes.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteNutrient = trpc.food.nutrientTypes.delete.useMutation({
    onSuccess: () => utils.food.nutrientTypes.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const createMeasurement = trpc.food.measurementTypes.create.useMutation({
    onSuccess: () => {
      utils.food.measurementTypes.list.invalidate();
      setAddMeasurementOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });
  const deleteMeasurement = trpc.food.measurementTypes.delete.useMutation({
    onSuccess: () => utils.food.measurementTypes.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const createAllergen = trpc.food.allergens.create.useMutation({
    onSuccess: () => utils.food.allergens.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteAllergen = trpc.food.allergens.delete.useMutation({
    onSuccess: () => utils.food.allergens.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-base font-semibold">Configuration</h1>
        <p className="text-sm text-muted-foreground">
          Schema definitions for the food application.
        </p>
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="formats" icon={<FileSpreadsheet className="h-3.5 w-3.5" />}>
            Nutritional Formats
          </TabsTrigger>
          <TabsTrigger value="nutrients" icon={<Apple className="h-3.5 w-3.5" />}>
            Nutrients
          </TabsTrigger>
          <TabsTrigger value="measurements" icon={<Ruler className="h-3.5 w-3.5" />}>
            Measurements
          </TabsTrigger>
          <TabsTrigger value="allergens" icon={<ShieldAlert className="h-3.5 w-3.5" />}>
            Allergens
          </TabsTrigger>
          <TabsTrigger value="equipment" icon={<Wrench className="h-3.5 w-3.5" />}>
            Equipment
          </TabsTrigger>
          <TabsTrigger value="sensory" icon={<FlaskConical className="h-3.5 w-3.5" />}>
            Sensory
          </TabsTrigger>
          <TabsTrigger value="brew-sensory" icon={<FlaskConical className="h-3.5 w-3.5" />}>
            Brew Sensory
          </TabsTrigger>
        </TabsList>

        <TabsContent value="nutrients">
          <div className="max-w-lg space-y-3">
            <div className="space-y-0.5">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Nutrient Types
              </h2>
              <p className="text-xs text-muted-foreground">
                Nutrients available when building ingredient profiles (per 100g).
              </p>
            </div>
            <TypeList<NutrientType>
              items={nutrientTypes ?? []}
              secondaryKey="default_unit"
              secondaryPlaceholder="Unit: g, mg, µg or kcal"
              namePlaceholder="e.g., Calories"
              onAdd={(name, unit) =>
                createNutrient.mutate({ name, default_unit: unit || "g" })
              }
              onDelete={(id) => deleteNutrient.mutate(id)}
              isAdding={createNutrient.isPending}
              isDeleting={deleteNutrient.isPending}
            />
          </div>
        </TabsContent>

        <TabsContent value="measurements">
          <div className="max-w-4xl space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-0.5">
                <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Measurement Types
                </h2>
                <p className="text-xs text-muted-foreground">
                  Physical, chemical, and sensory parameters used as project benchmarks. Link each measurement to a test template attribute to enable test-driven data entry in analysis.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-1.5"
                onClick={() => setAddMeasurementOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Measurement
              </Button>
            </div>

            <MeasurementTypeList<MeasurementType>
              items={measurementTypes ?? []}
              onDelete={(id) => deleteMeasurement.mutate(id)}
              isDeleting={deleteMeasurement.isPending}
            />

            <AddMeasurementDialog
              open={addMeasurementOpen}
              onOpenChange={setAddMeasurementOpen}
              isPending={createMeasurement.isPending}
              onAdd={(data) => createMeasurement.mutate(data)}
            />
          </div>
        </TabsContent>

        <TabsContent value="allergens">
          <div className="max-w-sm space-y-3">
            <div className="space-y-0.5">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Allergens
              </h2>
              <p className="text-xs text-muted-foreground">
                Allergens that can be assigned to ingredients and benchmarks.
              </p>
            </div>
            <TypeList<Allergen>
              items={allergens ?? []}
              namePlaceholder="e.g., Gluten"
              onAdd={(name) => createAllergen.mutate({ name })}
              onDelete={(id) => deleteAllergen.mutate(id)}
              isAdding={createAllergen.isPending}
              isDeleting={deleteAllergen.isPending}
            />
          </div>
        </TabsContent>

        <TabsContent value="formats">
          <div className="max-w-4xl">
            <FormatSection />
          </div>
        </TabsContent>

        <TabsContent value="equipment">
          <div className="max-w-xl">
            <EquipmentSection />
          </div>
        </TabsContent>

        <TabsContent value="sensory">
          <SensoryConfigSection />
        </TabsContent>

        <TabsContent value="brew-sensory">
          <BrewSensoryConfigSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
